import * as THREE from 'three';
import { ClipTask, DefaultColors } from '../defines';
import { GradientType } from '../materials/Gradients';
import type { ReactiveProperty, Subscription } from '@pointorama/potree-shared';
import {
  getSurfacePlanePositionProperty,
  getSurfacePlaneNormalProperty,
  getSurfaceToleranceProperty,
  getSurfacePlaneRotationProperty,
  getSurfaceKindProperty,
  getAnnotationColorProperty,
  getAnnotationVisibleProperty,
  getFilterToggleProperty,
  getClipMethodProperty,
  getPlaneHeatmapGradientProperty,
  getPlaneHeatmapIsolateProperty,
} from '@pointorama/potree-shared';
import { SurfaceKind } from 'shared-types';

// Re-export SurfaceKind for consumers of this module
export { SurfaceKind };

const DEFAULT_THICKNESS = 0.02;

export interface SurfacePlane {
  position: THREE.Vector3;
  normal: THREE.Vector3;
}

type Coordinate = { x: number; y: number; z: number };

export interface SurfaceConstructorArgs {
  identifier: string;
  plane?: SurfacePlane; // Optional fallback if reactive properties aren't available
  color?: THREE.Color;
  clipTask?: number;
  gradient?: GradientType;
  heatmapIsolate?: boolean;
}

/**
 * PointoramaSurface represents a surface defined by a plane (position + normal).
 * Visualized as a square outline, matching PointoramaPlane's circle style exactly.
 * Supports ClipTask-based highlighting for point cloud rendering.
 */
export class PointoramaSurface extends THREE.Object3D {
  private _identifier = '';
  finished = false;
  selected = false;
  boundingSphere?: THREE.Sphere;

  // Identifier getter/setter to trigger reactive property loading
  get identifier(): string {
    return this._identifier;
  }

  set identifier(value: string) {
    this._identifier = value;
    // Load reactive properties when identifier is set
    if (value) {
      this.loadReactiveProperties();
    }
  }

  private plane: SurfacePlane;

  // Square outline (4 edges as BoxGeometry thin boxes, like PointoramaPlane's torus)
  private squareEdges: THREE.Group;
  private edgeMaterial: THREE.MeshBasicMaterial;

  // Square fill (like PointoramaPlane's fullCircle)
  private squareFill: THREE.Mesh;
  private fillMaterial: THREE.MeshBasicMaterial;

  // Size and thickness properties (matching PointoramaPlane)
  radius: number;
  thickness: number = DEFAULT_THICKNESS;
  private lastAppliedThickness: number = DEFAULT_THICKNESS; // Track thickness for change detection
  frameColor: THREE.Color;

  // ClipTask properties for point cloud highlighting (matching PointoramaPlane)
  assignedClipTask: number;
  private _actualClipTask: number;
  gradient: GradientType = 'SPECTRAL';
  heatmapIsolate = false;
  /** Surfaces always clip in union mode (mirrors a plane with useIntersection=false).
   *  Read by PotreeRenderer when building the planeUseIntersection uniform. */
  useIntersection = false;

  // Filter state (kept in sync by reactive subscriptions, mirrors PointoramaPlane)
  private _filterEnabled = false;
  private _clipMethod = 'ISOLATE';
  private _filterToggleSubscription?: Subscription;
  private _clipMethodSubscription?: Subscription;
  private _heatmapGradientSubscription?: Subscription;
  private _heatmapIsolateSubscription?: Subscription;

  // Reactive properties for surface data (lazily loaded)
  private _positionProperty?: ReactiveProperty<Coordinate>;
  private _normalProperty?: ReactiveProperty<Coordinate>;
  private _toleranceProperty?: ReactiveProperty<number>;
  private _rotationProperty?: ReactiveProperty<number>;
  private _kindProperty?: ReactiveProperty<SurfaceKind>;

  // Subscriptions for reactive updates
  private _positionSubscription?: Subscription;
  private _normalSubscription?: Subscription;
  private _rotationSubscription?: Subscription;
  private _kindSubscription?: Subscription;
  private _colorSubscription?: Subscription;
  private _visibleSubscription?: Subscription;

  // Cached rotation value for orientation calculation
  private _rotation = 0;

  constructor(args: SurfaceConstructorArgs) {
    super();

    // Set identifier first
    this._identifier = args.identifier;

    // Initialize with provided plane or default plane (will be updated by loadReactiveProperties if not provided)
    this.plane = args.plane || {
      position: new THREE.Vector3(0, 0, 0),
      normal: new THREE.Vector3(0, 0, 1),
    };

    this.frameColor = args.color || DefaultColors.OBJECTS_THREE;
    this.radius = 1.0; // Default radius matching PointoramaPlane

    // Initialize ClipTask properties
    this.assignedClipTask = args.clipTask ?? ClipTask.NONE;
    this._actualClipTask = this.assignedClipTask;
    this.gradient = args.gradient ?? 'SPECTRAL';
    this.heatmapIsolate = args.heatmapIsolate ?? false;

    // Create edge material (matching PointoramaPlane's marker material)
    this.edgeMaterial = new THREE.MeshBasicMaterial({
      color: this.frameColor,
    });

    // Create square edges as a group of 4 thin boxes
    this.squareEdges = this.createSquareEdges();
    this.add(this.squareEdges);

    // Create square fill (invisible by default, used for selection/raycasting)
    const fillGeometry = new THREE.PlaneGeometry(this.radius * 2, this.radius * 2);
    this.fillMaterial = new THREE.MeshBasicMaterial({
      visible: false,
      side: THREE.DoubleSide,
    });
    this.squareFill = new THREE.Mesh(fillGeometry, this.fillMaterial);
    this.add(this.squareFill);

    // Load reactive properties and subscribe AFTER geometry is created
    // This ensures subscription callbacks can safely update position/orientation
    if (this._identifier) {
      this.loadReactiveProperties(args);
    }

    // Position and orient the surface (uses plane which was loaded from reactive properties)
    // Only set position/orientation if plane has valid values
    if (this.plane.position && !isNaN(this.plane.position.x) && !isNaN(this.plane.position.y) && !isNaN(this.plane.position.z)) {
      this.position.copy(this.plane.position);
      this.orientToNormal(this.plane.normal);
    }

    // Calculate bounding sphere
    this.updateBoundingSphere();
  }

  /**
   * Load reactive properties and update plane/tolerance from stored values
   * Called automatically in constructor when identifier is set
   * Also subscribes to property changes for automatic updates
   */
  private loadReactiveProperties(args: Partial<SurfaceConstructorArgs> = {}): void {
    if (!this._identifier) return;

    try {
      // Unsubscribe from previous properties if any
      this._positionSubscription?.unsubscribe();
      this._normalSubscription?.unsubscribe();
      this._rotationSubscription?.unsubscribe();
      this._kindSubscription?.unsubscribe();

      // Load reactive properties
      this._positionProperty = getSurfacePlanePositionProperty(this._identifier);
      this._normalProperty = getSurfacePlaneNormalProperty(this._identifier);
      this._toleranceProperty = getSurfaceToleranceProperty(this._identifier);
      this._rotationProperty = getSurfacePlaneRotationProperty(this._identifier);
      this._kindProperty = getSurfaceKindProperty(this._identifier);

      // Set surface-specific properties
      if (args.plane !== undefined) {
        const planePos = this.plane.position;
        this._positionProperty.value = { x: planePos.x, y: planePos.y, z: planePos.z };
        const planeNorm = this.plane.normal;
        this._normalProperty.value = { x: planeNorm.x, y: planeNorm.y, z: planeNorm.z };
        this._rotationProperty.value = this._rotation;
        this._kindProperty.value = this.surfaceKind;
      } else {
        const pos = this._positionProperty.value;
        if (pos && !isNaN(pos.x) && !isNaN(pos.y) && !isNaN(pos.z)) {
          this.plane.position.set(pos.x, pos.y, pos.z);
        }
        const norm = this._normalProperty.value;
        if (norm && !isNaN(norm.x) && !isNaN(norm.y) && !isNaN(norm.z)) {
          this.plane.normal.set(norm.x, norm.y, norm.z).normalize();
        }
        this._rotation = this._rotationProperty.value ?? this._rotation;
        // surfaceKind is read dynamically via its getter (uses _kindProperty.value)
      }

      // Subscribe to position changes
      this._positionSubscription = this._positionProperty.subscribeToChanges((newPos) => {
        if (newPos && !isNaN(newPos.x) && !isNaN(newPos.y) && !isNaN(newPos.z)) {
          this.plane.position.set(newPos.x, newPos.y, newPos.z);
          this.position.copy(this.plane.position);
          this.updateBoundingSphere();
        }
      });

      // Subscribe to normal changes
      this._normalSubscription = this._normalProperty.subscribeToChanges((newNorm) => {
        if (newNorm && !isNaN(newNorm.x) && !isNaN(newNorm.y) && !isNaN(newNorm.z)) {
          this.plane.normal.set(newNorm.x, newNorm.y, newNorm.z).normalize();
          this.orientToNormal(this.plane.normal);
        }
      });

      // Subscribe to rotation changes
      this._rotationSubscription = this._rotationProperty.subscribeToChanges((newRot) => {
        if (newRot !== undefined && !isNaN(newRot)) {
          this._rotation = newRot;
          this.orientToNormal(this.plane.normal);
        }
      });

      // Subscribe to kind changes (for external updates)
      this._kindSubscription = this._kindProperty.subscribeToChanges(() => {
        // Kind is metadata - no visual update needed, but subscription ensures reactivity
      });

      // --- Color ---
      this._colorSubscription?.unsubscribe();
      const colorProperty = getAnnotationColorProperty(this._identifier);
      if (args.color !== undefined) colorProperty.value = '#' + this.color.getHexString();
      else this.setColor(new THREE.Color(colorProperty.value));
      this._colorSubscription = colorProperty.subscribeToChanges((value) => {
        this.setColor(new THREE.Color(value));
      });

      // --- Visible ---
      this._visibleSubscription?.unsubscribe();
      const visibleProperty = getAnnotationVisibleProperty(this._identifier);
      // visible has no constructor arg — always read
      this.visible = visibleProperty.value;
      this._visibleSubscription = visibleProperty.subscribeToChanges((value) => {
        this.visible = value;
      });

      // --- Filter toggle + clip method (mirrors PointoramaPlane) ---
      // Surfaces become clip volumes exactly like planes once the user enables
      // the filter. These properties are keyed by the surface (group) identifier.
      this._filterToggleSubscription?.unsubscribe();
      const toggleProperty = getFilterToggleProperty(this._identifier);
      if (toggleProperty) {
        this._filterEnabled = toggleProperty.value;
        this._filterToggleSubscription = toggleProperty.subscribeToChanges((enabled) => {
          this._filterEnabled = enabled;
          this._updateClipTask();
        });
      }

      this._clipMethodSubscription?.unsubscribe();
      const clipMethodProperty = getClipMethodProperty(this._identifier);
      if (clipMethodProperty) {
        this._clipMethod = clipMethodProperty.value;
        this._clipMethodSubscription = clipMethodProperty.subscribeToChanges((method) => {
          this._clipMethod = method;
          this._updateClipTask();
        });
      }
      this._updateClipTask();

      // --- Heatmap gradient + isolate (shared identifiers, keyed by surface id) ---
      this._heatmapGradientSubscription?.unsubscribe();
      const gradientProp = getPlaneHeatmapGradientProperty(this._identifier);
      if (args.gradient !== undefined) gradientProp.value = this.gradient;
      else this.gradient = gradientProp.value as GradientType;
      this._heatmapGradientSubscription = gradientProp.subscribeToChanges((value) => {
        this.gradient = value as GradientType;
      });

      this._heatmapIsolateSubscription?.unsubscribe();
      const isolateProp = getPlaneHeatmapIsolateProperty(this._identifier);
      if (args.heatmapIsolate !== undefined) isolateProp.value = this.heatmapIsolate;
      else this.heatmapIsolate = isolateProp.value;
      this._heatmapIsolateSubscription = isolateProp.subscribeToChanges((value) => {
        this.heatmapIsolate = value;
      });

      // Note: tolerance and kind are read dynamically via their getters
    } catch (error) {
      // Global property service not initialized yet, will use default plane
      console.warn('Failed to load reactive properties for surface:', this._identifier, error);
    }
  }

  /**
   * Create the 4 edges of the square as thin box meshes
   */
  private createSquareEdges(): THREE.Group {
    const group = new THREE.Group();
    const size = this.radius * 2;
    const edgeThickness = this.thickness * this.radius * 4; // Match PointoramaPlane effective thickness

    // Create 4 edges using BoxGeometry
    // Bottom edge
    const bottomGeom = new THREE.BoxGeometry(size + edgeThickness, edgeThickness, edgeThickness);
    const bottomEdge = new THREE.Mesh(bottomGeom, this.edgeMaterial);
    bottomEdge.position.set(0, -this.radius, 0);
    group.add(bottomEdge);

    // Top edge
    const topGeom = new THREE.BoxGeometry(size + edgeThickness, edgeThickness, edgeThickness);
    const topEdge = new THREE.Mesh(topGeom, this.edgeMaterial);
    topEdge.position.set(0, this.radius, 0);
    group.add(topEdge);

    // Left edge
    const leftGeom = new THREE.BoxGeometry(edgeThickness, size + edgeThickness, edgeThickness);
    const leftEdge = new THREE.Mesh(leftGeom, this.edgeMaterial);
    leftEdge.position.set(-this.radius, 0, 0);
    group.add(leftEdge);

    // Right edge
    const rightGeom = new THREE.BoxGeometry(edgeThickness, size + edgeThickness, edgeThickness);
    const rightEdge = new THREE.Mesh(rightGeom, this.edgeMaterial);
    rightEdge.position.set(this.radius, 0, 0);
    group.add(rightEdge);

    return group;
  }

  /**
   * Orient the surface to face the given normal direction.
   * The top edge of the square will align with the horizon (horizontal),
   * then rotated by the _rotation angle around the normal.
   */
  private orientToNormal(normal: THREE.Vector3): void {
    // World up vector (Z-up coordinate system)
    const worldUp = new THREE.Vector3(0, 0, 1);

    // Calculate the "right" direction: cross product of world up and normal
    // This gives us a horizontal vector perpendicular to the normal
    const right = new THREE.Vector3().crossVectors(worldUp, normal);

    // Handle case where normal is parallel to world up (looking straight up or down)
    if (right.lengthSq() < 0.0001) {
      // Use world Y as fallback for right direction
      right.set(0, 1, 0);
    } else {
      right.normalize();
    }

    // Calculate the local "up" direction for the square (perpendicular to both normal and right)
    // This will be horizontal (aligned with horizon)
    const localUp = new THREE.Vector3().crossVectors(normal, right).normalize();

    // Build rotation matrix from the three orthogonal axes
    // right = local X, localUp = local Y, normal = local Z
    const rotationMatrix = new THREE.Matrix4().makeBasis(right, localUp, normal);
    this.quaternion.setFromRotationMatrix(rotationMatrix);

    // Apply additional rotation around the normal axis
    // Negate the rotation to match the camera roll direction (camera roll and gizmo rotation are inverse)
    if (this._rotation !== 0) {
      const rotationAroundNormal = new THREE.Quaternion().setFromAxisAngle(normal, -this._rotation);
      this.quaternion.premultiply(rotationAroundNormal);
    }
  }

  /**
   * Set the radius of the square (half the side length)
   */
  setRadius(radius: number): void {
    const radiusChanged = Math.abs(this.radius - radius) >= 0.0001;
    const thicknessChanged = Math.abs(this.lastAppliedThickness - this.thickness) >= 0.0001;

    // Skip if neither radius nor thickness has changed to avoid expensive geometry recreation every frame
    if (!radiusChanged && !thicknessChanged) {
      return;
    }

    this.radius = radius;
    this.lastAppliedThickness = this.thickness;

    // Recreate edges with new radius
    this.remove(this.squareEdges);
    this.squareEdges.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
      }
    });
    this.squareEdges = this.createSquareEdges();
    this.add(this.squareEdges);

    // Update fill geometry
    this.squareFill.geometry.dispose();
    this.squareFill.geometry = new THREE.PlaneGeometry(radius * 2, radius * 2);

    this.updateBoundingSphere();
  }

  /**
   * Set the thickness of the edges
   */
  setThickness(thickness: number): void {
    this.thickness = thickness;
    // Recreate edges with new thickness
    this.setRadius(this.radius);
  }

  set color(color: THREE.Color) {
    this.edgeMaterial.color = color;
    this.frameColor = color;
  }

  get color(): THREE.Color {
    return this.frameColor;
  }

  getPlane(): SurfacePlane {
    // Get latest values from reactive properties if available
    const pos = this._positionProperty?.value;
    const norm = this._normalProperty?.value;

    if (pos && norm) {
      return {
        position: new THREE.Vector3(pos.x, pos.y, pos.z),
        normal: new THREE.Vector3(norm.x, norm.y, norm.z),
      };
    }

    // Fallback to static plane data
    return this.plane;
  }

  /**
   * Get tolerance value from reactive property if available
   */
  get tolerance(): number {
    return this._toleranceProperty?.value ?? 0.01;
  }

  /**
   * Set tolerance value (updates reactive property if available)
   */
  set tolerance(value: number) {
    if (this._toleranceProperty) {
      this._toleranceProperty.value = value;
    }
  }

  /**
   * Get surface kind from reactive property if available
   * Returns SURFACE or SECTION
   */
  get surfaceKind(): SurfaceKind {
    return this._kindProperty?.value ?? SurfaceKind.SURFACE;
  }

  /**
   * Set surface kind (updates reactive property if available)
   */
  set surfaceKind(value: SurfaceKind) {
    if (this._kindProperty) {
      this._kindProperty.value = value;
    }
  }

  /**
   * Get rotation value (radians around the normal) from reactive property if available
   */
  get normalRotation(): number {
    return this._rotationProperty?.value ?? 0;
  }

  /**
   * Set rotation value (updates reactive property if available)
   */
  set normalRotation(value: number) {
    this._rotation = value;
    if (this._rotationProperty) {
      this._rotationProperty.value = value;
    }
    this.orientToNormal(this.plane.normal);
  }

  /**
   * Update the rotation via the reactive property system
   * This ensures the rotation is synced to the database
   */
  updateRotationProperty(): void {
    if (this._rotationProperty) {
      this._rotationProperty.value = this._rotation;
    }
  }

  /**
   * Get the normal vector of the surface (required for renderer compatibility)
   */
  getNormal(): THREE.Vector3 {
    return this.plane.normal.clone();
  }

  /**
   * Whether a point lies within `tolerance` of the surface plane.
   * Required by the renderer's node-clipping pass (Potree_update_visibility),
   * which calls this on every clip plane for SHOW_INSIDE / heatmap-isolate.
   * Mirrors PointoramaPlane.pointInsideClipArea — surfaces clip as infinite planes.
   */
  pointInsideClipArea(point: THREE.Vector3): boolean {
    const normal = this.plane.normal;
    const planeConstant = -normal.dot(this.plane.position);
    const plane = new THREE.Plane(normal, planeConstant);
    const distance = plane.distanceToPoint(point);
    return Math.abs(distance) <= this.tolerance;
  }

  /**
   * Orthogonally project a point onto the surface's plane.
   * Used by measure-tool projection snapping (mirrors PointoramaPlane).
   */
  getPointProjection(point: THREE.Vector3): THREE.Vector3 {
    const n = this.getNormal();
    const v = point.clone().sub(this.plane.position);
    const distance = v.dot(n);
    return point.clone().sub(n.multiplyScalar(distance));
  }

  /**
   * Acute angle between this surface's plane and another plane/surface.
   * Used by projection snapping to reject near-parallel planes (mirrors PointoramaPlane).
   */
  getAngleBetweenPlanes(other: { getNormal(): THREE.Vector3 }): number {
    return Math.abs(Math.acos(Math.abs(this.getNormal().dot(other.getNormal()))));
  }

  /**
   * Whether a point lies (within a tiny epsilon) on the surface's plane.
   * Used by measure angle-snapping to require points to be coplanar (mirrors PointoramaPlane).
   */
  containsPoint(point: THREE.Vector3): boolean {
    const distance = this.getNormal().dot(point.clone().sub(this.plane.position));
    return Math.abs(distance) <= 0.000001; // 1µm
  }

  setPlane(plane: SurfacePlane): void {
    this.plane = {
      position: plane.position.clone(),
      normal: plane.normal.clone().normalize(),
    };
    this.position.copy(this.plane.position);
    this.orientToNormal(this.plane.normal);
    this.updateBoundingSphere();
  }

  /**
   * Calculate and update the bounding sphere
   */
  private updateBoundingSphere(): void {
    // Diagonal of square with side 2*radius
    const diagonal = (this.radius * 2 * Math.sqrt(2)) / 2;
    this.boundingSphere = new THREE.Sphere(new THREE.Vector3(), diagonal);
  }

  /**
   * Get the color of the surface visualization
   */
  getColor(): THREE.Color {
    return this.frameColor;
  }

  /**
   * Set the color of the surface visualization
   */
  setColor(color: THREE.Color): void {
    this.frameColor = color;
    this.edgeMaterial.color = color;
  }

  /**
   * Select the surface (thicker outline)
   */
  select(): void {
    this.selected = true;
    this.thickness = 0.04;
    this.setRadius(this.radius); // Rebuild with new thickness
  }

  /**
   * Deselect the surface
   */
  deselect(): void {
    this.selected = false;
    this.thickness = DEFAULT_THICKNESS;
    this.setRadius(this.radius); // Rebuild with new thickness
  }

  /**
   * The clip task the renderer should apply. When the user has assigned a clip
   * task via the filter (assignedClipTask !== NONE) that wins; otherwise the
   * transient hover/highlight value applies. Mirrors PointoramaPlane.
   */
  get actualClipTask(): number {
    if (this.assignedClipTask === ClipTask.NONE) return this._actualClipTask;
    return this.assignedClipTask;
  }

  set actualClipTask(value: number) {
    this._actualClipTask = value;
  }

  /** Recompute assignedClipTask from the filter toggle + clip method properties. */
  private _updateClipTask(): void {
    this.assignedClipTask = this._filterEnabled ? this._clipMethodToTask(this._clipMethod) : ClipTask.NONE;
  }

  private _clipMethodToTask(clipMethod: string): number {
    switch (clipMethod) {
      case 'HIGHLIGHT':
        return ClipTask.GRAYSCALE;
      case 'HIDE':
        return ClipTask.SHOW_OUTSIDE;
      case 'ISOLATE':
        return ClipTask.SHOW_INSIDE;
      case 'HEATMAP':
        return ClipTask.HEATMAP;
      default:
        return ClipTask.NONE;
    }
  }

  /**
   * Highlight the surface (hover state) - increases thickness and enables ClipTask highlighting
   */
  highlight(): void {
    this.thickness = 0.03;
    this.setRadius(this.radius); // Rebuild with new thickness
    // Enable ClipTask highlighting for point cloud (like PointoramaPlane)
    if (this.assignedClipTask === ClipTask.NONE) {
      this._actualClipTask = ClipTask.HIGHLIGHT;
    }
  }

  /**
   * Remove highlight from the surface
   */
  unHighlight(): void {
    if (!this.selected) {
      this.thickness = DEFAULT_THICKNESS;
      this.setRadius(this.radius); // Rebuild with normal thickness
    }
    // Restore original ClipTask (like PointoramaPlane)
    this.actualClipTask = this.assignedClipTask;
  }

  /**
   * Get the selectable mesh for raycasting
   */
  getSelectableMesh(): THREE.Mesh {
    return this.squareFill;
  }

  /**
   * Update the position via the reactive property system
   * This ensures the position is synced to the database
   */
  updatePositionProperty(): void {
    if (this._positionProperty) {
      const pos = this.position;
      this._positionProperty.value = { x: pos.x, y: pos.y, z: pos.z };
    }
  }

  /**
   * Dispose of the surface resources
   */
  dispose(): void {
    // Unsubscribe from reactive properties
    this._positionSubscription?.unsubscribe();
    this._normalSubscription?.unsubscribe();
    this._rotationSubscription?.unsubscribe();
    this._kindSubscription?.unsubscribe();
    this._colorSubscription?.unsubscribe();
    this._visibleSubscription?.unsubscribe();
    this._filterToggleSubscription?.unsubscribe();
    this._clipMethodSubscription?.unsubscribe();
    this._heatmapGradientSubscription?.unsubscribe();
    this._heatmapIsolateSubscription?.unsubscribe();

    // Dispose THREE.js resources
    this.squareEdges.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
      }
    });
    this.edgeMaterial.dispose();
    this.squareFill.geometry.dispose();
    this.fillMaterial.dispose();
    this.remove(this.squareEdges);
    this.remove(this.squareFill);
  }
}
